/**
 * B2-backed case file browser (replaces the Google Drive / ElFinder iframe).
 * Renders the permission-filtered folder tree for a matter or contact from
 * GET /api/files/tree and drives all mutations through the injected `api`
 * (fileService by default).
 *
 * Tree node shape (from CaseFileService::nodePayload):
 *   folders: { id, name, kind: 'folder', restricted, modified, children: [...] }
 *   files:   { id, name, kind: 'file',   restricted, modified, ext, size }
 */

/* eslint-disable react/prop-types */ // Node row receives react-arborist's internal node API as props
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tree } from 'react-arborist';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Folder,
  FolderOpen,
  FolderPlus,
  History,
  Lock,
  Pencil,
  Search,
  Trash2,
  Upload,
  UserPlus,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { ConfirmationModal } from '@/components/modal';
import fileService from '@/services/files/fileService';
import { getApiErrorMessage } from '@/utils/ValidationErrors';

import { DOCUMENT_ACCEPT_ATTRIBUTE as ACCEPT_EXTENSIONS } from '@/config/documentTypes';
import { isInsideFolder, siblingFiles } from './tree';
import { VideoPlayer } from './VideoPlayer';
import { PdfViewer } from './PdfViewer';
import { ImageViewer } from './ImageViewer';
import { MAX_ZOOM, stepZoom, zoomLabel } from './imageZoom';

/* ------------------------------ presentation ----------------------------- */

const fmtSize = (bytes) => {
  if (bytes == null) return '';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

const fileIconFor = (ext) => {
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'tiff', 'tif', 'heic'].includes(ext)) return FileImage;
  if (['xlsx', 'xls', 'csv'].includes(ext)) return FileSpreadsheet;
  if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(ext)) return FileText;
  if (['mp4', 'mov', 'avi', 'wmv'].includes(ext)) return FileVideo;
  return File;
};

const uploadButtonLabel = (uploading, progress) => {
  if (!uploading) return 'Upload';
  if (!progress) return 'Uploading…';
  if (progress.phase === 'saving') return 'Saving…';
  return `Uploading ${Math.floor((progress.sent / progress.total) * 100)}%…`;
};

// The canonical extractor understands every error shape we throw — including
// blob bodies re-inflated by publicShareService.withReadableBlobError.
const apiErrorMessage = (error, fallback) => getApiErrorMessage(error, fallback);

/* One row. react-arborist gives us `node`, positioning `style`, and `dragHandle`. */
function Node({ node, style, dragHandle, onPreview, onDownload, onDownloadFolder, downloadFolderBusy, onHistory, onDelete, onShare, nodeBadge, readOnly }) {
  const data = node.data;
  const isFolder = node.isInternal;

  const FolderIcon = node.isOpen ? FolderOpen : Folder;
  const LeafIcon = fileIconFor(data.ext);

  return (
    <div
      ref={dragHandle}
      style={style}
      onClick={(e) => {
        // Ctrl/Cmd/Shift clicks are react-arborist's multi-select; leave them to it.
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        // The second click of a double-click: the first one already acted, so
        // a double-clicked folder opens once instead of opening and closing.
        if (e.detail > 1) {
          e.stopPropagation();
          return;
        }
        if (isFolder && node.isOpen) {
          // The tree's onToggle moves the upload target out of the closed
          // folder (OL-181). Stopped here, or react-arborist's own row click
          // selects the folder again.
          e.stopPropagation();
          node.close();
          return;
        }
        node.select();
        if (isFolder) node.open();
      }}
      // Double-click opens a file, like the eye button (OL-182). Not from the
      // row's buttons or the rename field.
      onDoubleClick={(e) => {
        if (!isFolder && !e.target.closest('button, input')) onPreview(data);
      }}
      className={clsx(
        'group flex items-center h-full pr-2 rounded-md cursor-pointer select-none',
        'hover:bg-gray-100',
        node.isSelected && 'bg-primary-light hover:bg-primary-light',
      )}
    >
      {/* caret: large and dark enough to read open vs closed at a glance (OL-180) */}
      <span className={clsx('w-6 shrink-0 flex justify-center', node.isOpen ? 'text-gray-900' : 'text-gray-600')}>
        {isFolder && (node.isOpen
          ? <ChevronDown size={18} strokeWidth={2.5} />
          : <ChevronRight size={18} strokeWidth={2.5} />)}
      </span>

      {/* icon: an open folder is filled */}
      <span className={clsx('shrink-0 mr-2', isFolder ? 'text-warning' : 'text-gray-500')}>
        {isFolder
          ? <FolderIcon size={18} fill={node.isOpen ? 'currentColor' : 'none'} fillOpacity={0.3} />
          : <LeafIcon size={16} />}
      </span>

      {/* name (or inline rename field) */}
      {node.isEditing ? (
        <input
          autoFocus
          defaultValue={data.name}
          className="input input-sm h-7 py-0 max-w-[280px]"
          onClick={(e) => e.stopPropagation()}
          onBlur={(e) => node.submit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') node.submit(e.currentTarget.value);
            if (e.key === 'Escape') node.reset();
          }}
        />
      ) : (
        <span className={clsx('truncate text-2sm text-gray-900', isFolder && node.isOpen && 'font-semibold')}>{data.name}</span>
      )}

      {data.restricted && (
        <Lock size={12} className="ml-1.5 shrink-0 text-gray-400" title="Restricted access" />
      )}

      {nodeBadge?.(data)}

      {/* right meta + hover actions */}
      <span className="ml-auto flex items-center gap-3 pl-3">
        {!isFolder && (
          <>
            <span className="text-2xs text-gray-500 tabular-nums hidden sm:inline">{fmtDate(data.modified)}</span>
            <span className="text-2xs text-gray-500 tabular-nums w-16 text-right hidden sm:inline">
              {fmtSize(data.size)}
            </span>
          </>
        )}
        <span className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Restricted nodes can't be shared; the server refuses them too. */}
          {onShare && !data.restricted && (
            <button
              className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-primary"
              title="Share with a client portal guest"
              onClick={(e) => {
                e.stopPropagation();
                onShare(data);
              }}
            >
              <UserPlus size={14} />
            </button>
          )}
          {isFolder && onDownloadFolder && data.can_download !== false && (
            <button
              className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-primary"
              title="Download folder (.zip)"
              disabled={downloadFolderBusy}
              onClick={(e) => {
                e.stopPropagation();
                onDownloadFolder(data);
              }}
            >
              <Download size={14} />
            </button>
          )}
          {!isFolder && (
            <>
              <button
                className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-primary"
                title="Preview"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview(data);
                }}
              >
                <Eye size={14} />
              </button>
              {onDownload && data.can_download !== false && (
                <button
                  className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-primary"
                  title="Download"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload(data);
                  }}
                >
                  <Download size={14} />
                </button>
              )}
              {!readOnly && (
                <button
                  className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-primary"
                  title="Version history"
                  onClick={(e) => {
                    e.stopPropagation();
                    onHistory(data);
                  }}
                >
                  <History size={14} />
                </button>
              )}
            </>
          )}
          {!readOnly && (
            <>
              <button
                className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-primary"
                title="Rename"
                onClick={(e) => {
                  e.stopPropagation();
                  node.edit();
                }}
              >
                <Pencil size={14} />
              </button>
              <button
                className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-danger"
                title="Delete (moves to recycle bin)"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(data);
                }}
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </span>
      </span>
    </div>
  );
}

/* --------------------------------- main ---------------------------------- */

/**
 * @param readOnly  hide every mutation control — used by the recipient-facing
 *                  shared-space page, where the server refuses writes anyway.
 * @param canDownload  false on a View Only share link (OL-166): no download
 *                  buttons, and files open in viewers without a download or
 *                  print control. The api must then provide getPdf(fileId).
 *                  A node or preview payload with `can_download: false`
 *                  narrows this per file (client portal, OL-195).
 * @param api       data source. Defaults to fileService (staff endpoints); the
 *                  share page passes an adapter for /api/shared/*. Memoize it,
 *                  or the load effect re-runs on every render.
 * @param onDownloadFolder  (nodeData) => saves the folder as a zip; the caller
 *                  owns busy state, errors, and the save. The hover button on
 *                  folder rows only renders when this is given.
 * @param downloadFolderBusy  disables every folder download button while any
 *                  zip (folder or whole-space) is in flight.
 * @param onTreeLoaded  called with the fresh node tree after every (re)load —
 *                  lets host pages derive counts without a second request.
 * @param onShare   (nodeData) => opens the host's share dialog. The hover
 *                  button on file and folder rows only renders when given
 *                  (client portal, OL-194).
 * @param nodeBadge (nodeData) => a small element shown after the node's name,
 *                  e.g. the "Shared" chip. Optional.
 */
const FileBrowser = ({
  fileableType,
  fileableId,
  readOnly = false,
  canDownload = true,
  api = fileService,
  onDownloadFolder,
  downloadFolderBusy = false,
  onTreeLoaded,
  onShare,
  nodeBadge,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState(null); // { id, kind, parentId, parentName, name }
  const treeRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Only set for large files, which go up in pieces (OL-164).
  const [uploadProgress, setUploadProgress] = useState(null); // { sent, total, phase }

  const [pendingDelete, setPendingDelete] = useState(null); // { id, name }
  const [folderModal, setFolderModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [preview, setPreview] = useState(null); // { id, name, url, mime, inline }
  const [previewBusy, setPreviewBusy] = useState(false);
  // Image zoom, kept while stepping to the next file and reset on close (OL-241).
  const [imageZoom, setImageZoom] = useState(1);
  const previewRef = useRef(null);
  // Bumped on every preview request and on close, so a slow response for a
  // file the user has already moved past (or closed) is dropped.
  const previewRequest = useRef(0);
  const [versionsFor, setVersionsFor] = useState(null); // { id, name }
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const uploadInputRef = useRef(null);
  const versionInputRef = useRef(null);

  // Ref, not a dependency: host pages pass inline arrows, and a fresh identity
  // per render must not re-trigger the load effect.
  const onTreeLoadedRef = useRef(onTreeLoaded);
  useEffect(() => {
    onTreeLoadedRef.current = onTreeLoaded;
  });

  const load = useCallback(async () => {
    if (!fileableType || !fileableId) return;
    try {
      const tree = await api.getTree(fileableType, fileableId);
      setData(tree ?? []);
      onTreeLoadedRef.current?.(tree ?? []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load files.', {
        description: apiErrorMessage(error, 'An error occurred while loading files. Please try again later.'),
      });
    } finally {
      setLoading(false);
    }
  }, [fileableType, fileableId, api]);

  useEffect(() => {
    setLoading(true);
    setSelected(null);
    load();
  }, [load]);

  /* Where new folders / uploads land: the selected folder, or the selected
     file's parent folder, or the root. */
  const targetFolderId = selected ? (selected.kind === 'folder' ? selected.id : selected.parentId) : null;
  const targetFolderName = selected ? (selected.kind === 'folder' ? selected.name : selected.parentName) : null;

  /* ------------------------------ handlers ------------------------------ */

  const onSelect = useCallback((nodes) => {
    const node = nodes[0];
    if (!node) return setSelected(null);
    // A top-level node's parent is the tree library's own root, whose id is
    // not a folder: sending it as the parent made uploads fail (OL-181).
    const parent = node.parent && !node.parent.isRoot ? node.parent.data : null;
    setSelected({
      id: node.id,
      kind: node.data.kind,
      parentId: parent?.id ?? null,
      parentName: parent?.name ?? null,
      name: node.data.name,
    });
  }, []);

  /* After every reload (rename, move, delete, upload) read the selection back
     from the tree, so the target and its "Uploads go to" name follow the
     change. A selected node that is gone sends uploads to the top level. */
  useEffect(() => {
    if (treeRef.current) onSelect(treeRef.current.selectedNodes);
  }, [data, onSelect]);

  /* A folder closed, by click or keyboard. If the upload target was that folder
     or inside it, move it out to the folder it sits in, so the next upload
     doesn't land in a folder the user has just shut (OL-181). */
  const onToggle = useCallback((id) => {
    const tree = treeRef.current;
    const folder = tree?.get(id);
    if (!folder || folder.isOpen) return;
    if (!tree.selectedNodes.some((node) => isInsideFolder(node, id))) return;
    if (folder.parent && !folder.parent.isRoot) folder.parent.select();
    else tree.deselectAll();
  }, []);

  const uploadToTopLevel = () => {
    treeRef.current?.deselectAll();
    setSelected(null);
  };

  const onRename = async ({ id, name }) => {
    try {
      await api.rename(id, name);
    } catch (error) {
      toast.error('Rename failed.', { description: apiErrorMessage(error, 'Please try again.') });
    }
    load();
  };

  const onMove = async ({ dragIds, parentId }) => {
    try {
      for (const id of dragIds) {
        await api.move(id, parentId);
      }
    } catch (error) {
      toast.error('Move failed.', { description: apiErrorMessage(error, 'Please try again.') });
    }
    load();
  };

  const confirmDelete = async () => {
    const node = pendingDelete;
    setPendingDelete(null);
    try {
      await api.remove(node.id);
      toast.success(`"${node.name}" moved to the recycle bin.`);
    } catch (error) {
      toast.error('Delete failed.', { description: apiErrorMessage(error, 'Please try again.') });
    }
    if (selected?.id === node.id) setSelected(null);
    load();
  };

  const createFolder = async () => {
    const name = folderName.trim();
    if (!name) return;
    setFolderModal(false);
    setFolderName('');
    try {
      await api.createFolder({ fileableType, fileableId, parentId: targetFolderId, name });
      toast.success(`Folder "${name}" created.`);
    } catch (error) {
      toast.error('Could not create folder.', { description: apiErrorMessage(error, 'Please try again.') });
    }
    load();
  };

  const onUploadFiles = async (fileList) => {
    const files = Array.from(fileList ?? []);
    if (!files.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        await api.uploadFile({
          fileableType,
          fileableId,
          parentId: targetFolderId,
          file,
          onProgress: setUploadProgress,
        });
        uploaded += 1;
      } catch (error) {
        toast.error(`Failed to upload "${file.name}".`, {
          description: apiErrorMessage(error, 'The file may be too large or of an unsupported type.'),
        });
      }
      setUploadProgress(null);
    }
    setUploading(false);
    if (uploaded) toast.success(`Uploaded ${uploaded} file${uploaded > 1 ? 's' : ''}.`);
    load();
  };

  /* Opening a file that can't be shown inline downloads it, as before. Stepping
     onto one from the open viewer shows a download panel instead (OL-151). */
  const showInViewer = async (fileId, stepping = false) => {
    const request = ++previewRequest.current;
    setPreviewBusy(true);
    try {
      const payload = await api.getPreview(fileId);
      if (request !== previewRequest.current) return;
      if (payload.inline || stepping) {
        setPreview({
          id: fileId,
          name: payload.name,
          url: payload.url,
          mime: payload.mime,
          inline: payload.inline,
          canDownload: canDownload && payload.can_download !== false,
        });
      } else {
        // Not inline-previewable — pre-signed attachment URL triggers a download.
        window.open(payload.url, '_blank');
      }
    } catch (error) {
      toast.error('Preview failed.', { description: apiErrorMessage(error, 'Please try again.') });
    } finally {
      if (request === previewRequest.current) setPreviewBusy(false);
    }
  };

  const openPreview = (nodeData) => showInViewer(nodeData.id);

  // Stable per file, so the PDF viewer fetches once rather than on every render.
  const previewId = preview?.id;
  const loadPdf = useCallback(() => api.getPdf(previewId), [api, previewId]);

  const closePreview = () => {
    previewRequest.current += 1;
    setPreviewBusy(false);
    setPreview(null);
    setImageZoom(1);
  };

  // Previous/next step through the open file's folder, like Google Drive (OL-151).
  const previewSiblings = useMemo(
    () => (preview ? siblingFiles(data, preview.id, term) : []),
    [data, preview, term],
  );
  const previewIndex = preview ? previewSiblings.findIndex((file) => file.id === preview.id) : -1;
  const prevFile = previewIndex > 0 ? previewSiblings[previewIndex - 1] : null;
  const nextFile = previewIndex >= 0 ? previewSiblings[previewIndex + 1] ?? null : null;

  const stepPreview = (target) => {
    if (target && !previewBusy) showInViewer(target.id, true);
  };

  // The key listener is added once per open; it reads the latest handlers here.
  const previewIsImage = Boolean(preview?.inline && preview?.mime?.startsWith('image/'));
  const zoomImage = (direction) => setImageZoom((zoom) => stepZoom(zoom, direction));

  const previewKeys = useRef({});
  previewKeys.current = {
    Escape: closePreview,
    ArrowLeft: () => stepPreview(prevFile),
    ArrowRight: () => stepPreview(nextFile),
    ...(previewIsImage && {
      '+': () => zoomImage(1),
      '=': () => zoomImage(1),
      '-': () => zoomImage(-1),
      0: () => setImageZoom(1),
    }),
  };

  const previewOpen = Boolean(preview);
  useEffect(() => {
    if (!previewOpen) return undefined;
    // Take focus off the tree, whose own arrow keys would also fire.
    previewRef.current?.focus();
    const onKey = (event) => {
      const handler = previewKeys.current[event.key];
      if (!handler) return;
      // A focused video uses the arrow keys to seek.
      if (event.target?.tagName === 'VIDEO' && event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      handler();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [previewOpen]);

  const download = async (nodeData) => {
    try {
      const url = await api.getDownloadUrl(nodeData.id);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Download failed.', { description: apiErrorMessage(error, 'Please try again.') });
    }
  };

  const openHistory = async (nodeData) => {
    setVersionsFor({ id: nodeData.id, name: nodeData.name });
    loadVersions(nodeData.id);
  };

  const loadVersions = async (id) => {
    setVersionsLoading(true);
    try {
      setVersions(await api.getVersions(id));
    } catch (error) {
      toast.error('Failed to load version history.', { description: apiErrorMessage(error, 'Please try again.') });
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const uploadNewVersion = async (fileList) => {
    const file = fileList?.[0];
    if (!file || !versionsFor) return;
    setVersionsLoading(true);
    try {
      await api.uploadVersion(versionsFor.id, file);
      toast.success('New version uploaded.');
      await loadVersions(versionsFor.id);
      load();
    } catch (error) {
      toast.error('Failed to upload new version.', { description: apiErrorMessage(error, 'Please try again.') });
      setVersionsLoading(false);
    }
  };

  const restoreVersion = async (versionId) => {
    setVersionsLoading(true);
    try {
      await api.restoreVersion(versionsFor.id, versionId);
      toast.success('Version restored.');
      await loadVersions(versionsFor.id);
      load();
    } catch (error) {
      toast.error('Failed to restore version.', { description: apiErrorMessage(error, 'Please try again.') });
      setVersionsLoading(false);
    }
  };

  /* ------------------------------- render ------------------------------- */

  if (loading) {
    return <p className="text-center text-2sm text-gray-600 py-10">Loading files…</p>;
  }

  return (
    <div className="w-full">
      {/* toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative w-full max-w-[260px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Filter files…"
            className="input input-sm w-full pl-9"
          />
        </div>
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Where Upload and New folder put things, next to the buttons that do it (OL-181). */}
            <span
              className="flex items-center gap-1.5 rounded-md bg-gray-100 px-2.5 py-1.5 text-xs text-gray-700"
              aria-live="polite"
              title={targetFolderId ? undefined : 'Select a folder to upload into it.'}
            >
              <Folder size={14} className="text-warning" />
              Uploads go to:
              <span className="max-w-[180px] truncate font-semibold text-gray-900">{targetFolderId ? targetFolderName : 'Top level'}</span>
              {targetFolderId && (
                <button
                  type="button"
                  className="ml-0.5 text-gray-500 hover:text-gray-900"
                  onClick={uploadToTopLevel}
                  title="Upload to the top level instead"
                  aria-label="Upload to the top level instead"
                >
                  <X size={13} />
                </button>
              )}
            </span>
            <button className="btn btn-sm btn-light" onClick={() => setFolderModal(true)}>
              <FolderPlus size={15} /> New folder
            </button>
            <button
              className="btn btn-sm btn-primary"
              disabled={uploading}
              onClick={() => uploadInputRef.current?.click()}
            >
              <Upload size={15} /> {uploadButtonLabel(uploading, uploadProgress)}
            </button>
            <input
              ref={uploadInputRef}
              type="file"
              multiple
              accept={ACCEPT_EXTENSIONS}
              className="hidden"
              onChange={(e) => {
                onUploadFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
        )}
      </div>

      {/* tree */}
      {data.length === 0 ? (
        <p className="text-center text-2sm text-gray-500 py-10">
          {readOnly ? (
            'There are no documents here yet.'
          ) : (
            <>
              No files yet. Use <span className="font-medium">Upload</span> to add the first document.
            </>
          )}
        </p>
      ) : (
        <Tree
          ref={treeRef}
          data={data}
          idAccessor="id"
          openByDefault={false}
          searchTerm={term}
          searchMatch={(node, t) => node.data.name.toLowerCase().includes(t.toLowerCase())}
          width="100%"
          height={480}
          rowHeight={40}
          indent={22}
          paddingBottom={12}
          onRename={readOnly ? undefined : onRename}
          onMove={readOnly ? undefined : onMove}
          onSelect={onSelect}
          onToggle={onToggle}
          disableEdit={readOnly}
          disableDrag={readOnly}
          disableDrop={readOnly}
        >
          {(props) => (
            <Node
              {...props}
              readOnly={readOnly}
              onPreview={openPreview}
              onDownload={canDownload ? download : undefined}
              onDownloadFolder={onDownloadFolder}
              downloadFolderBusy={downloadFolderBusy}
              onHistory={openHistory}
              onDelete={(nodeData) => setPendingDelete({ id: nodeData.id, name: nodeData.name })}
              onShare={onShare}
              nodeBadge={nodeBadge}
            />
          )}
        </Tree>
      )}

      {/* delete confirmation */}
      <ConfirmationModal
        show={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Move to recycle bin?"
        message={`"${pendingDelete?.name}" will be moved to the recycle bin. Folders are moved together with everything inside them.`}
      />

      {/* new folder */}
      {folderModal && (
        <div className="modal-wrapper modal-animate-fade">
          <div className="modal-body modal-animate-scale w-4/5 max-w-md">
            <button type="button" onClick={() => setFolderModal(false)} className="modal-close-btn">
              <X size={16} />
            </button>
            <h2 className="text-lg font-medium text-gray-900 my-2">New folder</h2>
            <input
              autoFocus
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createFolder()}
              placeholder="Folder name"
              className="input w-full"
            />
            <div className="flex items-start justify-end gap-2">
              <button onClick={() => setFolderModal(false)} className="btn btn-outline mt-3">
                Cancel
              </button>
              <button onClick={createFolder} disabled={!folderName.trim()} className="btn btn-primary mt-3">
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* inline preview */}
      {preview && (
        <div
          ref={previewRef}
          tabIndex={-1}
          className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 modal-animate-fade outline-none"
        >
          <button
            type="button"
            onClick={() => stepPreview(prevFile)}
            disabled={!prevFile || previewBusy}
            aria-label="Previous file"
            title="Previous file (Left arrow)"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 size-11 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={24} />
          </button>
          {/* Fills the screen like Google Drive's viewer, leaving room for the arrows (OL-185). */}
          <div className="modal-body modal-animate-scale !px-5 flex flex-col w-[calc(100vw-1rem)] md:w-[calc(100vw-9rem)] max-w-6xl h-[calc(100vh-2rem)] overflow-hidden">
            <div className="flex justify-between items-center shrink-0">
              <h2 className="text-lg font-medium text-gray-900 my-2 truncate pr-6">{preview.name}</h2>
              <div className="flex items-center gap-3 shrink-0">
                {previewIsImage && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => zoomImage(-1)}
                      disabled={imageZoom <= 1}
                      aria-label="Zoom out"
                      title="Zoom out (-)"
                      className="btn btn-xs btn-icon btn-light"
                    >
                      <ZoomOut size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageZoom(1)}
                      title="Fit to window (0)"
                      className="btn btn-xs btn-light min-w-12 justify-center"
                    >
                      {zoomLabel(imageZoom)}
                    </button>
                    <button
                      type="button"
                      onClick={() => zoomImage(1)}
                      disabled={imageZoom >= MAX_ZOOM}
                      aria-label="Zoom in"
                      title="Zoom in (+)"
                      className="btn btn-xs btn-icon btn-light"
                    >
                      <ZoomIn size={14} />
                    </button>
                  </div>
                )}
                {previewSiblings.length > 1 && (
                  <span className="text-xs text-gray-500">
                    {previewIndex + 1} of {previewSiblings.length}
                  </span>
                )}
                <button type="button" onClick={closePreview} className="modal-close-btn">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 flex items-center justify-center overflow-auto">
              {!preview.inline ? (
                <div className="flex flex-col items-center justify-center gap-3 py-20 text-sm text-gray-600">
                  <File size={40} className="text-gray-400" />
                  <p>This file type can&apos;t be previewed here.</p>
                  {preview.canDownload && (
                    <a href={preview.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-primary">
                      <Download size={14} /> Download
                    </a>
                  )}
                </div>
              ) : preview.mime?.startsWith('video/') ? (
                <VideoPlayer
                  src={preview.url}
                  title={preview.name}
                  allowDownload={preview.canDownload}
                  onRefresh={async () => (await api.getPreview(preview.id)).url}
                />
              ) : preview.mime?.startsWith('image/') ? (
                <ImageViewer
                  src={preview.url}
                  alt={preview.name}
                  zoom={imageZoom}
                  onZoomChange={setImageZoom}
                  canDownload={preview.canDownload}
                />
              ) : !preview.canDownload && preview.mime === 'application/pdf' ? (
                // The browser's PDF viewer has download and print buttons.
                <PdfViewer load={loadPdf} />
              ) : (
                <iframe
                  // Open PDFs at page width without the thumbnail sidebar, instead of a small whole-page view.
                  src={preview.mime === 'application/pdf' ? `${preview.url}#view=FitH&navpanes=0` : preview.url}
                  title={preview.name}
                  className="w-full h-full border-0"
                />
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => stepPreview(nextFile)}
            disabled={!nextFile || previewBusy}
            aria-label="Next file"
            title="Next file (Right arrow)"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 size-11 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}

      {/* version history */}
      {versionsFor && (
        <div className="modal-wrapper modal-animate-fade">
          <div className="modal-body modal-animate-scale w-4/5 max-w-2xl">
            <button type="button" onClick={() => setVersionsFor(null)} className="modal-close-btn">
              <X size={16} />
            </button>
            <div className="flex items-center justify-between pr-8">
              <h2 className="text-lg font-medium text-gray-900 my-2 truncate">
                Version history — {versionsFor.name}
              </h2>
              <button
                className="btn btn-sm btn-light shrink-0"
                disabled={versionsLoading}
                onClick={() => versionInputRef.current?.click()}
              >
                <Upload size={14} /> Upload new version
              </button>
              <input
                ref={versionInputRef}
                type="file"
                accept={ACCEPT_EXTENSIONS}
                className="hidden"
                onChange={(e) => {
                  uploadNewVersion(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>
            {versionsLoading ? (
              <p className="text-center text-2sm text-gray-600 py-6">Loading…</p>
            ) : versions.length === 0 ? (
              <p className="text-center text-2sm text-gray-500 py-6">No version history recorded.</p>
            ) : (
              <div className="max-h-[50vh] overflow-y-auto mt-2">
                {versions.map((v, i) => (
                  <div
                    key={v.id}
                    className="flex items-center gap-3 py-2 border-b border-gray-200 last:border-0 text-2sm"
                  >
                    <span className="badge badge-sm badge-outline shrink-0">
                      {i === 0 ? 'Current' : `v${versions.length - i}`}
                    </span>
                    <span className="text-gray-700">{fmtDateTime(v.created_at)}</span>
                    <span className="text-gray-500">{fmtSize(v.size)}</span>
                    {v.uploaded_by && <span className="text-gray-500 truncate">by {v.uploaded_by}</span>}
                    {i > 0 && (
                      <button
                        className="btn btn-xs btn-light ml-auto shrink-0"
                        onClick={() => restoreVersion(v.id)}
                      >
                        <History size={13} /> Restore
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { FileBrowser };
